import classNames from 'classnames';
import { ResizeObserverContainer } from 'components/ResizeObserverContainer/ResizeObserverContainer';
import {
  type FC,
  type TouchEvent,
  type WheelEvent,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import './ScrollingWrapper.css';

type Props = {
  children: ReactNode;
  arrowsWidth: number;
};

export const ScrollingWrapper: FC<Props> = ({ children, arrowsWidth }) => {
  const STEP = 30;
  const ARROW_HEIGHT = 20;

  const [showVerticalArrows, setShowVerticalArrows] = useState(false);
  const [topArrowIsActive, setTopArrowIsActive] = useState(false);
  const [bottomArrowIsActive, setBottomArrowIsActive] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const dragging = useRef(false);
  const dragStartY = useRef(0);
  const scrollTopStart = useRef(0);
  const distance = useRef<number | null>(null);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
  const detectHeightOverflow = () => {
    const content = contentRef.current?.firstElementChild as HTMLElement | null;
    const container = containerRef.current;

    if (container && content) {
      const { offsetHeight: containerOffsetHeight } = container || {};
      const { scrollHeight: childScrollHeight } = content || {};

      if (childScrollHeight > containerOffsetHeight) {
        const marginTop = Number.parseInt(content.style.marginTop, 10);

        setShowVerticalArrows(true);
        if (!marginTop || !Number.isNaN(marginTop)) {
          content.style.marginTop = '20px';
        }

        // ARROW_HEIGHT * 1.5 is a fixed offset is added to the scrollHeight to ensure that
        // there is enough space to show the vertical arrows at the top and bottom of the
        // container without overlapping with the content.
        const offset = distance.current ? ARROW_HEIGHT : ARROW_HEIGHT * 1.5;
        distance.current = childScrollHeight + offset - containerOffsetHeight;
      } else {
        distance.current = null;
        setShowVerticalArrows(false);
        content.style.marginTop = '';
      }
    }
  };

  const checkArrowsState = () => {
    const content = contentRef.current?.firstElementChild as HTMLElement | null;
    const marginTop = content && Number.parseInt(content.style.marginTop, 10);

    if (distance.current !== null) {
      setTopArrowIsActive(!!content && !!marginTop && marginTop < ARROW_HEIGHT);
      setBottomArrowIsActive(
        !!content && !!marginTop && marginTop > -distance.current,
      );
    }
  };

  const scrollTop = (step = STEP) => {
    const content = contentRef.current?.firstElementChild as HTMLElement | null;

    if (content) {
      const marginTop = Number.parseInt(content.style.marginTop, 10);
      const to = Math.min(marginTop + step, ARROW_HEIGHT);

      content.style.marginTop = `${to}px`;
      checkArrowsState();
    }
  };

  const scrollBottom = (step = STEP) => {
    const content = contentRef.current?.firstElementChild as HTMLElement | null;

    if (content && distance.current !== null) {
      const marginTop = Number.parseInt(content.style.marginTop, 10);
      const to = Math.max(marginTop - step, -distance.current);
      content.style.marginTop = `${to}px`;
      checkArrowsState();
    }
  };

  const scrollTo = (position: number) => {
    const content = contentRef.current?.firstElementChild as HTMLElement | null;

    if (content) {
      const to = (() => {
        if (position > ARROW_HEIGHT) {
          return ARROW_HEIGHT;
        }
        if (distance.current !== null && position < -distance.current) {
          return -distance.current;
        }
        return position;
      })();

      content.style.marginTop = `${to}px`;
      checkArrowsState();
    }
  };

  const handleTouchStart = (event: TouchEvent) => {
    const content = contentRef.current;

    if (!content || !showVerticalArrows) {
      return;
    }

    dragging.current = true;
    // Fixed noUncheckedIndexedAccess NIM-19408
    dragStartY.current = event.touches[0]!.clientY;
    scrollTopStart.current = content?.scrollTop || 0;
    content.style.transition = 'unset';
  };

  const handleTouchMove = (event: TouchEvent) => {
    const content = contentRef.current;

    if (!dragging.current || !content) {
      return;
    }

    // Fixed noUncheckedIndexedAccess NIM-19408
    const deltaY = dragStartY.current - event.touches[0]!.clientY;
    scrollTo(-deltaY);
  };

  const handleTouchEnd = () => {
    const content = contentRef.current;

    if (!content) {
      return;
    }

    dragging.current = false;
    content.style.transition = '';
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    const content = contentRef.current;

    if (content && showVerticalArrows) {
      if (event.deltaY > 0) {
        scrollBottom(event.deltaY);
      } else {
        scrollTop(-event.deltaY);
      }
    }
  };

  return (
    <ResizeObserverContainer
      className={classNames(
        'scrolling-wrapper__container',
        showVerticalArrows && 'scrolling-wrapper__container--vertical',
      )}
      containerRef={containerRef}
      onResize={detectHeightOverflow}
    >
      {showVerticalArrows && (
        <div
          className={classNames(
            'scrolling-wrapper__arrow--top',
            !topArrowIsActive && 'disabled',
          )}
          onClick={() => scrollTop()}
          style={{ width: arrowsWidth }}
        >
          <i className='fa fa-chevron-up' />
        </div>
      )}
      <div
        className='scrolling-wrapper__content'
        ref={contentRef}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
      {showVerticalArrows && (
        <div
          className={classNames(
            'scrolling-wrapper__arrow--bottom',
            !bottomArrowIsActive && 'disabled',
          )}
          onClick={() => scrollBottom()}
          style={{ width: arrowsWidth }}
        >
          <i className='fa fa-chevron-down' />
        </div>
      )}
    </ResizeObserverContainer>
  );
};
